import { faker } from '@faker-js/faker';
import { CouponOfferType } from '@shared/constants';

const couponTypes: CouponOfferType[] = [
  CouponOfferType.BASKET,
  CouponOfferType.PRODUCT,
  CouponOfferType.CATEGORY,
];

export type CouponState = 'notSelected' | 'selected' | 'redeemed';

interface MockCouponOptions {
  state?: CouponState;
  offerType?: CouponOfferType;
  offerId?: number;
  label?: string | null;
  rank?: number;
  minimumProductSpend?: number;
  amountReward?: number;
}

export class MockCoupon {
  offer_id: number;
  campaign_id: number;
  label: string | null;
  rank: number;
  minimum_units: null;
  minimum_product_spend: number;
  amount_reward: number;
  percentage_reward: null;
  fixed_price: null;
  offer_type: CouponOfferType;
  reward_earned: number;
  loading_date: string | null;
  redemption_date: string | null;
  additional_information: string | null =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed non risus. Suspendisse lectus tortor, dignissim sit amet, adipiscing nec, ultricies sed, dolor. Cras elementum ultrices diam. Maecenas ligula massa, varius a, semper congue, euismod non, mi.';
  logo_path: string = 'assets/images/default/logo.svg';
  remaining_selections: number = faker.number.int({ min: 100, max: 9999 });

  constructor(isSelected = false, options: MockCouponOptions = {}) {
    const state = options.state ?? this.getState(isSelected);

    this.offer_id = options.offerId ?? faker.number.int({ min: 300, max: 999 });
    this.campaign_id = faker.number.int({ min: 100, max: 299 });
    this.offer_type =
      options.offerType ?? faker.helpers.arrayElement(couponTypes);
    this.label = options.label ?? this.getLabel(this.offer_type);
    this.rank = options.rank ?? 0;
    this.minimum_units = null;
    this.minimum_product_spend =
      options.minimumProductSpend ??
      faker.helpers.arrayElement([20, 30, 50, 75, 100]);
    this.amount_reward =
      options.amountReward ?? Math.floor(this.minimum_product_spend / 10);
    this.percentage_reward = null;
    this.fixed_price = null;
    this.loading_date =
      state === 'notSelected'
        ? null
        : faker.date.recent({ days: 14 }).toISOString();
    this.redemption_date =
      state === 'redeemed'
        ? faker.date.recent({ days: 7 }).toISOString()
        : null;
    this.reward_earned = state === 'redeemed' ? this.amount_reward : 0;
  }

  private getState(isSelected: boolean): CouponState {
    if (!isSelected) {
      return 'notSelected';
    }

    return faker.helpers.arrayElement(['selected', 'redeemed']);
  }

  private getLabel(offerType: CouponOfferType): string | null {
    if (offerType === CouponOfferType.BASKET) {
      return null;
    }

    if (offerType === CouponOfferType.CATEGORY) {
      return faker.commerce.department();
    }

    return faker.commerce.productName();
  }
}

type CouponStateCounts = Record<CouponState, number>;

// Default dataset when no counts are given: every state populated so all three
// coupons-page tabs ("En cours" / "À découvrir" / "Terminées") are shown.
const DEFAULT_COUPON_COUNTS: CouponStateCounts = {
  notSelected: 12,
  selected: 6,
  redeemed: 6,
};

export class CouponsResponseParams {
  notSelected: number;
  selected: number;
  redeemed: number;

  // Counts per state map to the tabs: notSelected -> "À découvrir",
  // selected -> "En cours", redeemed -> "Terminées". Any state left out is 0.
  constructor(counts: Partial<CouponStateCounts> = DEFAULT_COUPON_COUNTS) {
    this.notSelected = counts.notSelected ?? 0;
    this.selected = counts.selected ?? 0;
    this.redeemed = counts.redeemed ?? 0;
  }
}

export const generateCoupons: (
  params?: CouponsResponseParams
) => Array<MockCoupon> = (
  params: CouponsResponseParams = new CouponsResponseParams()
): Array<MockCoupon> => {
  // Deterministic dataset: labels, minimum spends and reward amounts are all
  // rendered on the coupons page, so an unseeded faker would make every
  // Playwright screenshot differ from its reference (same rationale as
  // `generateRewards`).
  faker.seed(123);

  const countsByState: Array<[CouponState, number]> = [
    ['selected', params.selected],
    ['redeemed', params.redeemed],
    ['notSelected', params.notSelected],
  ];

  const coupons: MockCoupon[] = [];
  let index = 0;

  for (const [state, count] of countsByState) {
    for (let i = 0; i < count; i++) {
      coupons.push(
        new MockCoupon(false, {
          state,
          offerId: 1000 + index,
          offerType: couponTypes[index % couponTypes.length],
        })
      );
      index++;
    }
  }

  return coupons;
};
